import { state } from '../state';
import { get, post } from '../api';
import { showToast } from '../components/toast';
import { showContextMenu } from '../components/context-menu';
import type { ContextMenuItem } from '../components/context-menu';
import { openProfileCard } from '../components/profile-card';
import {
  renderDevSessionsWidget,
  cleanupDevSessionWidgets,
} from '../channels/dev-sessions';
import {
  renderMarkdown,
  showComposeEmojiPicker,
  openGifPicker,
  openStickerPicker,
  showMentionAutocomplete,
  showSlashAutocomplete,
  showChannelAutocomplete,
  showEmojiAutocomplete,
  handleMentionKeydown,
  handleSlashKeydown,
  handleEmojiKeydown,
} from '../channels/chat';
import { avatarBg } from '../util';
import { fmtDayHeading, fmtMessageHeaderTime } from '../util/time';
import {
  mountMessageCompose,
  type ComposeHandle,
} from '../components/message-compose';
import { realtime, dmInboxTopic } from '../realtime';
import { notify, maybePromptOnFirstDmSend } from '../components/notifications';

interface DmConversation {
  person_id: string;
  person_name: string;
  last_message: string;
  last_time: string;
  unread: number;
}

interface DirectMessage {
  id: string;
  from_id: string;
  to_id: string;
  content: string;
  read_at: string | null;
  created_at: string;
}

function esc(s: string): string {
  const el = document.createElement('div');
  el.textContent = s;
  return el.innerHTML;
}

/** Per-message header timestamp in the DM message feed. Delegates to
 *  util/time.fmtMessageHeaderTime so DMs, main chat, and threads share one
 *  Discord-1:1 render (HUB_TZ-correct day buckets + leading-zero numeric
 *  older fallback "DD/MM/YYYY 3:31 pm"). The prior implementation used
 *  bare new Date() + WebView-locale toLocaleDateString/Time with no tz
 *  arg and no leading zeros, drifting on non-en-AU systems. */
function formatTime(iso: string): string {
  return fmtMessageHeaderTime(iso);
}

// Compact format for the DM inbox row right-side cell (.dm-item-time).
// Discord's sidebar uses bare time today, "Yesterday", short weekday for the
// last 7 days, and a numeric date for older — keeps the narrow cell readable
// instead of forcing "Today at 1:23 PM" into a flex-shrink:0 column.
function formatDmListTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const dKey = d.toDateString();
  if (dKey === now.toDateString()) {
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }
  if (dKey === yesterday.toDateString()) return 'Yesterday';
  const ageDays = (now.getTime() - d.getTime()) / 86400000;
  if (ageDays < 7) return d.toLocaleDateString([], { weekday: 'short' });
  return d.toLocaleDateString();
}

function authorName(id: string): string {
  return state.people.find(p => p.id === id)?.name || `Member-${id.substring(0, 6)}`;
}

let dmPollTimer: ReturnType<typeof setInterval> | null = null;
let dmComposeHandle: ComposeHandle | null = null;

/** Currently-open DM pair (for realtime append vs badge routing). */
let openDmContext: { me: string; partner: string; messagesEl: HTMLElement } | null = null;
/** Inbox topic the SSE stream is currently subscribed to — tracked so we
 *  unsubscribe cleanly if the signed-in identity ever changes mid-session. */
let subscribedInboxId: string | null = null;
/** Disposer for the realtime listener registered on first DM render. */
let dmRealtimeOff: (() => void) | null = null;

function ensureDmRealtime(myId: string): void {
  if (!myId) return;

  if (subscribedInboxId !== myId) {
    if (subscribedInboxId) realtime.unsubscribeChannel(dmInboxTopic(subscribedInboxId));
    realtime.subscribeChannel(dmInboxTopic(myId));
    subscribedInboxId = myId;
    realtime.connect();
  }

  if (dmRealtimeOff) return;
  dmRealtimeOff = realtime.on((ev) => {
    if (ev.type !== 'dm_created') return;
    const dm = ev.dm;
    const inOpenConvo = openDmContext
      && ((dm.from_id === openDmContext.me && dm.to_id === openDmContext.partner)
        || (dm.from_id === openDmContext.partner && dm.to_id === openDmContext.me));
    if (inOpenConvo && openDmContext) {
      // Re-render the open conversation so the new message lands without a reload.
      void loadDmMessages(openDmContext.me, openDmContext.partner, openDmContext.messagesEl);
      return;
    }
    // Inbound DM from someone else, not in the open conversation — fire
    // a desktop notification (tab gating + permission happen inside notify).
    if (dm.from_id !== myId) {
      const sender = state.people.find(p => p.id === dm.from_id);
      const senderName = sender?.name || authorName(dm.from_id);
      const avatar = sender?.avatar || undefined;
      notify({
        kind: 'dm',
        title: senderName,
        body: dm.content,
        icon: avatar,
        onClick: () => openDmConversation(dm.from_id, myId),
      });
    }
    // Not in the open conversation — refresh the DM home list so the
    // unread badge + last-message preview update. Safe no-op when dm-list
    // isn't rendered (user is viewing a non-DM channel).
    const listEl = document.getElementById('dm-list');
    if (listEl) void loadConversations(myId);
  });
}

/** Render the DM home view (conversation list) */
export function renderDmHome(container: HTMLElement): void {
  if (dmPollTimer) { clearInterval(dmPollTimer); dmPollTimer = null; }

  const myId = state.ownerId || state.people[0]?.id || '';
  ensureDmRealtime(myId);

  // Discord-parity home: Direct Messages first, with the optional admin
  // dashboard widgets only when the operator has explicitly opted in via
  // `hp_home_admin_widgets`. Default OFF — non-admins should never see
  // "Active Dev Sessions" or VPS stats on a chat-app home.
  const showAdminWidgets = state.flags['hp_home_admin_widgets'] === true;
  const adminBlock = showAdminWidgets ? `
      <section class="dm-section dm-dashboard">
        <h3 class="dm-section-title">Active Dev Sessions</h3>
        <div id="dm-dev-sessions"></div>
      </section>
      <section class="dm-section dm-dashboard">
        <h3 class="dm-section-title">VPS Stats</h3>
        <div id="dm-vps-stats"></div>
      </section>` : '';

  // Discord-parity home: "Friends" tab header with Online / All / Pending /
  // Blocked / Add Friend sub-tabs, then DM conversation list below.
  const savedSubTab = localStorage.getItem('hub-people-dm-friends-tab') || 'online';
  container.innerHTML = `
    <div class="dm-home">
      <div class="dm-header dm-friends-header">
        <button class="dm-home-menu ca-mobile-only" id="dm-home-menu" aria-label="Open server list" type="button">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
        </button>
        <div class="dm-friends-title">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
          <span>Friends</span>
        </div>
        <div class="dm-friends-subtabs" role="tablist">
          <button class="dm-subtab ${savedSubTab === 'online' ? 'active' : ''}" data-subtab="online" role="tab">Online</button>
          <button class="dm-subtab ${savedSubTab === 'all' ? 'active' : ''}" data-subtab="all" role="tab">All</button>
          <button class="dm-subtab ${savedSubTab === 'pending' ? 'active' : ''}" data-subtab="pending" role="tab">Pending</button>
          <button class="dm-subtab ${savedSubTab === 'blocked' ? 'active' : ''}" data-subtab="blocked" role="tab">Blocked</button>
          <button class="dm-subtab dm-subtab-add ${savedSubTab === 'add' ? 'active' : ''}" data-subtab="add" role="tab">Add Friend</button>
        </div>
      </div>
      <div class="dm-search">
        <input type="text" class="dm-search-input" placeholder="Find or start a conversation" id="dm-search">
      </div>
      <div class="dm-friends-panel" id="dm-friends-panel"></div>
      <div class="dm-section-label">Direct Messages</div>
      <div class="dm-list" id="dm-list">
        <div class="chat-loading">Loading conversations...</div>
      </div>
      ${adminBlock}
    </div>
  `;

  if (showAdminWidgets) {
    const dsHost = document.getElementById('dm-dev-sessions');
    if (dsHost) renderDevSessionsWidget(dsHost);
    const vsHost = document.getElementById('dm-vps-stats');
    if (vsHost && state.flags['vps_stats_widget'] !== false) renderVpsStatsWidget(vsHost);
    else if (vsHost) vsHost.parentElement?.remove();
  }

  void loadConversations(myId);
  renderFriendsPanel(savedSubTab, myId);

  // Mobile menu button — opens the server rail + channel list drawer
  document.getElementById('dm-home-menu')?.addEventListener('click', () => {
    document.getElementById('server-rail')?.classList.add('open');
    document.getElementById('channel-list')?.classList.add('open');
    document.getElementById('sidebar-scrim')?.classList.add('open');
  });

  // Sub-tab wiring — persist the selection and re-render the friends panel.
  container.querySelectorAll<HTMLElement>('.dm-subtab').forEach(btn => {
    btn.addEventListener('click', () => {
      const key = btn.dataset['subtab'] || 'online';
      container.querySelectorAll('.dm-subtab').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      localStorage.setItem('hub-people-dm-friends-tab', key);
      renderFriendsPanel(key, myId);
    });
  });

  // DM search — filter conversations and show user matches
  const searchInput = document.getElementById('dm-search') as HTMLInputElement;
  searchInput?.addEventListener('input', () => {
    const query = searchInput.value.toLowerCase().trim();
    const listEl = document.getElementById('dm-list');
    if (!listEl) return;

    if (!query) {
      void loadConversations(myId);
      return;
    }

    // Filter people matching query
    const matches = state.people.filter(p =>
      p.id !== myId && (
        p.name.toLowerCase().includes(query) ||
        (p.email || '').toLowerCase().includes(query)
      )
    ).slice(0, 10);

    if (matches.length === 0) {
      listEl.innerHTML = '<div class="dm-empty"><p style="color:var(--fg-muted)">No matches found</p></div>';
      return;
    }

    listEl.innerHTML = '<div class="dm-section-label">People</div>' + matches.map(p => {
      const initial = (p.name || '?').charAt(0).toUpperCase();
      return `<div class="dm-item" data-person="${esc(p.id)}">
        <div class="dm-item-avatar">${esc(initial)}</div>
        <div class="dm-item-info">
          <div class="dm-item-name">${esc(p.name)}</div>
          <div class="dm-item-preview">Click to message</div>
        </div>
      </div>`;
    }).join('');

    // Wire click handlers
    listEl.querySelectorAll<HTMLElement>('.dm-item[data-person]').forEach(el => {
      el.addEventListener('click', () => {
        const pid = el.dataset['person'];
        if (pid) openDmConversation(pid, myId);
      });
    });
  });
}

/**
 * Render the Discord-parity Friends panel for the selected sub-tab.
 * Online / All come off `state.people` + presence. Pending / Blocked are
 * stubbed (no API yet) so users see the tab structure + a clear empty state
 * rather than a broken screen. Add Friend is a simple form that routes into
 * the existing DM open flow.
 */
function renderFriendsPanel(subTab: string, myId: string): void {
  const el = document.getElementById('dm-friends-panel');
  if (!el) return;
  // Discord parity: Friends panel lists HUMAN friends only — bots (Architect /
  // CC / dev1 assistants) are ecosystem fixtures, not friends. Same family of
  // bug as the server rail bot-space leak (9f1292d); left un-filtered the
  // Online tab in a fresh tenant shows only bots as "friends".
  const others = state.people.filter(p => p.id !== myId && p.role !== 'bot');
  const online = others.filter(p => p.status === 'active');
  const listPeople = (people: typeof others, label: string, emptyMsg: string): string => {
    if (people.length === 0) {
      return `<div class="dm-friends-empty-zen">
        <div class="dm-friends-empty-zen-title">${esc(emptyMsg)}</div>
      </div>`;
    }
    // Discord label format: "ONLINE — 5" / "ALL FRIENDS — 12" (uppercase, count inline)
    return `<div class="dm-friends-count">${esc(label)} &mdash; ${people.length}</div>`
      + people.map(p => {
          const initial = (p.name || '?').charAt(0).toUpperCase();
          const status = p.status === 'active' ? 'Online' : 'Offline';
          const dot = p.status === 'active' ? 'dm-friend-dot-online' : 'dm-friend-dot-offline';
          return `<div class="dm-friend-row" data-person="${esc(p.id)}" tabindex="0" role="button" aria-label="${esc(p.name)}">
            <div class="dm-friend-avatar-wrap">
              <div class="dm-friend-avatar">${esc(initial)}</div>
              <span class="dm-friend-status-dot ${dot}"></span>
            </div>
            <div class="dm-friend-info">
              <div class="dm-friend-name">${esc(p.name)}</div>
              <div class="dm-friend-status">${esc(status)}</div>
            </div>
            <div class="dm-friend-actions">
              <button class="dm-friend-act-btn" data-act="msg" data-person-act="${esc(p.id)}" title="Send Message" aria-label="Send Message">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M20 2H4a2 2 0 0 0-2 2v18l4-4h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2z"/></svg>
              </button>
              <button class="dm-friend-act-btn" data-act="more" data-person-act="${esc(p.id)}" title="More" aria-label="More">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/></svg>
              </button>
            </div>
          </div>`;
        }).join('');
  };

  if (subTab === 'online') {
    el.innerHTML = listPeople(online, 'Online', 'No one is online right now.');
  } else if (subTab === 'all') {
    el.innerHTML = listPeople(others, 'All Friends', 'Nobody here yet.');
  } else if (subTab === 'pending') {
    // Discord parity: split incoming + outgoing requests, with Accept/Decline
    // on incoming and a Cancel on outgoing. Backend may not have shipped the
    // friend_requests endpoints yet, so degrade silently to the empty zen
    // state — the UI surface stays consistent either way.
    el.innerHTML = `<div class="dm-friends-empty-zen">
      <div class="dm-friends-empty-zen-title">There are no pending friend requests.</div>
    </div>`;
    void import('../api').then(async ({ get, post }) => {
      try {
        const reqs = await get<Array<{ id: string; from_id: string; from_name: string; to_id: string; to_name: string; direction: 'incoming' | 'outgoing'; created_at: string }>>(
          `/api/friends/requests?person_id=${encodeURIComponent(myId)}`,
        );
        if (!Array.isArray(reqs) || reqs.length === 0) return;
        const incoming = reqs.filter(r => r.direction === 'incoming');
        const outgoing = reqs.filter(r => r.direction === 'outgoing');
        let html = '';
        if (incoming.length > 0) {
          html += `<div class="dm-friends-count">Incoming &mdash; ${incoming.length}</div>`;
          html += incoming.map(r => {
            const initial = (r.from_name || '?').charAt(0).toUpperCase();
            return `<div class="dm-friend-row dm-friend-pending" data-req-id="${esc(r.id)}">
              <div class="dm-friend-avatar-wrap">
                <div class="dm-friend-avatar" style="background:${avatarBg(r.from_id)}">${esc(initial)}</div>
              </div>
              <div class="dm-friend-info">
                <div class="dm-friend-name">${esc(r.from_name)}</div>
                <div class="dm-friend-status">Incoming Friend Request</div>
              </div>
              <div class="dm-friend-actions dm-friend-actions-pinned">
                <button class="dm-friend-act-btn dm-friend-act-accept" data-req-accept="${esc(r.id)}" title="Accept" aria-label="Accept">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                </button>
                <button class="dm-friend-act-btn dm-friend-act-decline" data-req-decline="${esc(r.id)}" title="Decline" aria-label="Decline">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                </button>
              </div>
            </div>`;
          }).join('');
        }
        if (outgoing.length > 0) {
          html += `<div class="dm-friends-count">Outgoing &mdash; ${outgoing.length}</div>`;
          html += outgoing.map(r => {
            const initial = (r.to_name || '?').charAt(0).toUpperCase();
            return `<div class="dm-friend-row dm-friend-pending" data-req-id="${esc(r.id)}">
              <div class="dm-friend-avatar-wrap">
                <div class="dm-friend-avatar" style="background:${avatarBg(r.to_id)}">${esc(initial)}</div>
              </div>
              <div class="dm-friend-info">
                <div class="dm-friend-name">${esc(r.to_name)}</div>
                <div class="dm-friend-status">Outgoing Friend Request</div>
              </div>
              <div class="dm-friend-actions dm-friend-actions-pinned">
                <button class="dm-friend-act-btn dm-friend-act-decline" data-req-decline="${esc(r.id)}" title="Cancel" aria-label="Cancel">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                </button>
              </div>
            </div>`;
          }).join('');
        }
        el.innerHTML = html;
        el.querySelectorAll<HTMLButtonElement>('[data-req-accept]').forEach(btn => {
          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const id = btn.dataset['reqAccept'];
            if (!id) return;
            void post(`/api/friends/requests/${encodeURIComponent(id)}/accept`, {})
              .then(() => { showToast('Friend request accepted', 'ok'); renderFriendsPanel('pending', myId); })
              .catch(() => showToast('Could not accept request', 'err'));
          });
        });
        el.querySelectorAll<HTMLButtonElement>('[data-req-decline]').forEach(btn => {
          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const id = btn.dataset['reqDecline'];
            if (!id) return;
            void post(`/api/friends/requests/${encodeURIComponent(id)}/decline`, {})
              .then(() => { showToast('Friend request declined', 'ok'); renderFriendsPanel('pending', myId); })
              .catch(() => showToast('Could not decline request', 'err'));
          });
        });
      } catch {
        // backend not ready — keep zen empty state.
      }
    });
  } else if (subTab === 'blocked') {
    // Discord parity: show every user the viewer has blocked, with an Unblock
    // button. Source of truth is the localStorage `hub-people-blocked` set
    // managed by the profile-card "Block messages from this user" toggle.
    let blockedIds: string[] = [];
    try { blockedIds = JSON.parse(localStorage.getItem('hub-people-blocked') || '[]') as string[]; } catch { blockedIds = []; }
    const blocked = others.filter(p => blockedIds.includes(p.id));
    if (blocked.length === 0) {
      el.innerHTML = `<div class="dm-friends-empty-zen">
        <div class="dm-friends-empty-zen-title">You haven't blocked anyone.</div>
      </div>`;
    } else {
      el.innerHTML = `<div class="dm-friends-count">Blocked &mdash; ${blocked.length}</div>`
        + blocked.map(p => {
            const initial = (p.name || '?').charAt(0).toUpperCase();
            return `<div class="dm-friend-row dm-friend-blocked" data-person-blocked="${esc(p.id)}">
              <div class="dm-friend-avatar-wrap">
                <div class="dm-friend-avatar dm-friend-avatar-blocked">${esc(initial)}</div>
              </div>
              <div class="dm-friend-info">
                <div class="dm-friend-name">${esc(p.name)}</div>
                <div class="dm-friend-status">Blocked</div>
              </div>
              <div class="dm-friend-actions dm-friend-actions-pinned">
                <button class="dm-friend-act-btn" data-unblock="${esc(p.id)}" title="Unblock" aria-label="Unblock">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M12 2a6 6 0 0 0-6 6v3H5a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-1V8a6 6 0 0 0-6-6zm0 2a4 4 0 0 1 4 4v3H8V8a4 4 0 0 1 4-4z"/></svg>
                </button>
              </div>
            </div>`;
          }).join('');
      el.querySelectorAll<HTMLButtonElement>('[data-unblock]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const pid = btn.dataset['unblock'];
          if (!pid) return;
          const cur = new Set<string>(JSON.parse(localStorage.getItem('hub-people-blocked') || '[]') as string[]);
          cur.delete(pid);
          localStorage.setItem('hub-people-blocked', JSON.stringify([...cur]));
          // Un-hide their messages anywhere in the DOM right now.
          document.querySelectorAll<HTMLElement>(`.msg[data-author="${pid}"]`).forEach(el => { el.style.display = ''; });
          // Re-render the panel.
          renderFriendsPanel('blocked', myId);
        });
      });
    }
  } else if (subTab === 'add') {
    el.innerHTML = `
      <div class="dm-add-friend">
        <div class="dm-add-title">ADD FRIEND</div>
        <div class="dm-add-hint">You can add friends with their name or email.</div>
        <form class="dm-add-form" id="dm-add-form">
          <div class="dm-add-input-wrap">
            <input class="dm-add-input" id="dm-add-input" type="text" placeholder="You can add friends with their name or email." maxlength="120" autocomplete="off">
            <button class="dm-add-submit" id="dm-add-submit" type="submit" disabled>Send Friend Request</button>
          </div>
        </form>
        <div class="dm-add-result" id="dm-add-result"></div>
      </div>`;
    const form = document.getElementById('dm-add-form') as HTMLFormElement | null;
    const input = document.getElementById('dm-add-input') as HTMLInputElement | null;
    const submit = document.getElementById('dm-add-submit') as HTMLButtonElement | null;
    const result = document.getElementById('dm-add-result');
    // Discord parity: the submit button is disabled until the input has content.
    // Enable-on-input keeps the visual affordance honest (you can see when
    // the form is actually submittable).
    input?.addEventListener('input', () => {
      if (submit) submit.disabled = !(input.value || '').trim();
      if (result) result.textContent = '';
    });
    form?.addEventListener('submit', (e) => {
      e.preventDefault();
      const q = (input?.value || '').trim();
      if (!q) return;
      const match = others.find(p =>
        p.name.toLowerCase() === q.toLowerCase()
        || (p.email || '').toLowerCase() === q.toLowerCase()
        || p.name.toLowerCase().includes(q.toLowerCase()));
      if (!match) {
        if (result) {
          result.classList.add('dm-add-result-err');
          result.textContent = `No match for "${q}". Check the name or email and try again.`;
        }
        return;
      }
      // Backend may not have shipped /api/friends/requests yet — fall back to
      // opening a DM conversation so the user has *some* path through. When
      // the endpoint exists, we record the request and toast confirmation.
      void post('/api/friends/requests', { from_id: myId, to_id: match.id })
        .then(() => {
          showToast(`Friend request sent to ${match.name}`, 'ok');
          if (result) {
            result.classList.remove('dm-add-result-err');
            result.classList.add('dm-add-result-ok');
            result.textContent = `Sent! ${match.name} will see your request in Pending.`;
          }
          if (input) input.value = '';
          if (submit) submit.disabled = true;
        })
        .catch(() => { openDmConversation(match.id, myId); });
    });
    input?.focus();
  }

  // Wire friend-row click → open DM conversation. Keyboard Enter/Space on the
  // row (tabindex=0) mirrors the click path for screen-reader / keyboard users.
  el.querySelectorAll<HTMLElement>('.dm-friend-row').forEach(row => {
    const pid = row.dataset['person'];
    if (!pid) return;
    row.addEventListener('click', () => openDmConversation(pid, myId));
    row.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openDmConversation(pid, myId);
      }
    });
  });

  // Per-action button clicks — stop propagation so we don't double-fire the
  // row open-DM handler above.
  el.querySelectorAll<HTMLButtonElement>('.dm-friend-act-btn[data-act]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const pid = btn.dataset['personAct'];
      if (!pid) return;
      const kind = btn.dataset['act'];
      if (kind === 'msg') {
        openDmConversation(pid, myId);
      } else if (kind === 'more') {
        const rect = btn.getBoundingClientRect();
        showContextMenu(rect.right, rect.bottom, [
          { label: 'Send Message', action: () => openDmConversation(pid, myId) },
          { label: 'View Profile', action: () => openProfileCard(pid, btn) },
          { divider: true, label: '' },
          { label: 'Block', danger: true, action: () => {
              const cur = new Set<string>(JSON.parse(localStorage.getItem('hub-people-blocked') || '[]') as string[]);
              cur.add(pid);
              localStorage.setItem('hub-people-blocked', JSON.stringify([...cur]));
              showToast('Blocked', 'ok');
              renderFriendsPanel(subTab, myId);
            }
          },
        ]);
      }
    });
  });
}

async function loadConversations(myId: string): Promise<void> {
  const listEl = document.getElementById('dm-list');
  if (!listEl) return;

  try {
    const convos = await get<DmConversation[]>(
      `/api/dms/conversations?person_id=${encodeURIComponent(myId)}`,
    );

    if (convos.length === 0) {
      listEl.innerHTML = `
        <div class="dm-empty dm-empty-illus">
          <svg class="dm-empty-svg" viewBox="0 0 64 64" width="80" height="80" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M40 12c4 0 7 3 7 7v3"/>
            <path d="M40 12l-4 8 6 4 4-8"/>
            <path d="M22 30c-2 0-4 2-4 4v14a4 4 0 0 0 4 4h22a4 4 0 0 0 4-4V34c0-2-2-4-4-4H22z"/>
            <circle cx="28" cy="42" r="1.5" fill="currentColor"/>
            <circle cx="34" cy="42" r="1.5" fill="currentColor"/>
            <circle cx="40" cy="42" r="1.5" fill="currentColor"/>
          </svg>
          <p class="dm-empty-title">No friends yet</p>
          <p class="dm-empty-hint">Add Friend below to start a conversation.</p>
        </div>`;

      // Show all people as potential DM targets
      const people = state.people.filter(p => p.id !== myId);
      if (people.length > 0) {
        let html = '<div class="dm-section-label">Start a conversation</div>';
        html += people.map(p => {
          const initial = (p.name || '?').charAt(0).toUpperCase();
          return `<div class="dm-item" data-person="${esc(p.id)}">
            <div class="dm-item-avatar" style="background:${avatarBg(p.id)}">${esc(initial)}</div>
            <div class="dm-item-info">
              <div class="dm-item-name">${esc(p.name)}</div>
              <div class="dm-item-preview">Click to message</div>
            </div>
          </div>`;
        }).join('');
        listEl.innerHTML += html;
      }
    } else {
      listEl.innerHTML = convos.map(c => {
        const initial = (c.person_name || '?').charAt(0).toUpperCase();
        const unread = c.unread > 0 ? `<span class="dm-badge">${c.unread}</span>` : '';
        return `<div class="dm-item ${c.unread > 0 ? 'dm-unread' : ''}" data-person="${esc(c.person_id)}">
          <div class="dm-item-avatar" style="background:${avatarBg(c.person_id)}">${esc(initial)}</div>
          <div class="dm-item-info">
            <div class="dm-item-name">${esc(c.person_name)} ${unread}</div>
            <div class="dm-item-preview">${esc(c.last_message)}</div>
          </div>
          <div class="dm-item-time">${esc(formatDmListTime(c.last_time))}</div>
        </div>`;
      }).join('');
    }

    // Click handler to open DM conversation
    listEl.querySelectorAll<HTMLElement>('.dm-item').forEach(el => {
      el.addEventListener('click', () => {
        const personId = el.dataset['person'] ?? '';
        if (personId) openDmConversation(personId, myId);
      });
    });
  } catch {
    listEl.innerHTML = '<div class="dm-empty">Failed to load conversations</div>';
  }
}

/**
 * Open a 1:1 DM conversation. Exposed so the global `hub-open-dm` event
 * (dispatched from member-list/profile-card "Send Message" actions) can route
 * to the conversation view (1e/1f).
 */
export function openDmConversation(partnerId: string, myId?: string): void {
  const me = myId || state.ownerId || state.people[0]?.id || '';
  if (!me) { showToast('Not signed in', 'err'); return; }

  const container = document.getElementById('content-area');
  if (!container) return;

  if (dmPollTimer) { clearInterval(dmPollTimer); dmPollTimer = null; }
  dmComposeHandle?.destroy();
  dmComposeHandle = null;
  ensureDmRealtime(me);

  // DMs are 1:1 — never show the server MEMBERS panel here. Clear + hide
  // the right-side #member-list aside so a stale server roster doesn't
  // render alongside the DM Conversation panel (bug report batch 7q).
  const ml = document.getElementById('member-list');
  if (ml) {
    ml.innerHTML = '';
    ml.classList.remove('open');
    ml.classList.remove('mobile-open');
  }
  // Also drop the shared scrim + any "members toggle" active state left
  // over from the previous server view so the DM lands clean.
  document.getElementById('sidebar-scrim')?.classList.remove('open');
  document.getElementById('members-toggle')?.classList.remove('active');

  const partnerName = authorName(partnerId);
  const partnerInitial = (partnerName || '?').charAt(0).toUpperCase();

  container.innerHTML = `
    <div class="ca-header">
      <button class="ca-header-back ca-mobile-only" id="dm-header-back" aria-label="Back to Direct Messages" type="button">
        <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/></svg>
      </button>
      <span class="dm-conv-avatar" style="background:${avatarBg(partnerId)}">${esc(partnerInitial)}</span>
      <span class="ca-header-title">${esc(partnerName)}</span>
      <span class="ca-header-divider"></span>
      <span class="ca-header-topic">Direct Message</span>
      <div class="ca-header-actions">
        <button class="ca-header-btn" id="dm-members-toggle" title="Toggle conversation panel" aria-label="Toggle conversation panel" aria-pressed="true">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg>
        </button>
      </div>
    </div>
    <div class="ca-body" style="display:flex;flex:1;min-height:0">
      <div class="chat-container" style="flex:1;display:flex;flex-direction:column;min-width:0">
        <div class="chat-messages" id="dm-messages">
          <div class="chat-loading">Loading...</div>
        </div>
        <div class="dm-compose-mount" id="dm-compose-mount"></div>
        <button class="chat-scroll-btn" id="dm-scroll-btn" style="display:none" title="Jump to latest" aria-label="Jump to latest">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/></svg>
        </button>
      </div>
      <aside class="dm-partner-panel" id="dm-partner-panel">
        <div class="ml-header">
          <span>Conversation</span>
          <button class="ml-close-btn" id="dm-partner-close" title="Hide panel" aria-label="Hide panel">&times;</button>
        </div>
        <div class="dm-partner-card" data-id="${esc(partnerId)}" title="View profile">
          <div class="dm-partner-avatar" style="background:${avatarBg(partnerId)}">${esc(partnerInitial)}</div>
          <div class="dm-partner-name">${esc(partnerName)}</div>
        </div>
        <div class="ml-group-label">Mutual Servers</div>
        <div class="dm-mutual-servers" id="dm-mutual-servers"><span style="color:var(--fg-muted);font-size:12px;padding:4px 10px">Loading…</span></div>
        <div class="ml-group-label">Mutual Friends</div>
        <div class="dm-mutual-friends" id="dm-mutual-friends"><span style="color:var(--fg-muted);font-size:12px;padding:4px 10px">Loading…</span></div>
      </aside>
    </div>
  `;

  // Discord parity: fetch mutual servers + friends for the right panel.
  void loadMutualData(partnerId, me);

  const messagesEl = document.getElementById('dm-messages') as HTMLElement;
  openDmContext = { me, partner: partnerId, messagesEl };

  // Mount shared compose component.
  const composeMount = document.getElementById('dm-compose-mount');
  if (composeMount) {
    dmComposeHandle = mountMessageCompose(composeMount, {
      draftKey: `dm:${me}:${partnerId}`,
      dm_peer_id: partnerId,
      placeholder: `Message @${partnerName}`,
      maxLength: 4000,
      onSend: async (text) => {
        // Third gesture point for the notification prompt — fire
        // synchronously before any await so Android Chrome keeps the
        // click-stack context. No-op if permission is already resolved
        // or we've already prompted this user on DM send.
        maybePromptOnFirstDmSend();
        await sendDm(me, partnerId, text, messagesEl);
      },
      onInput: (textarea) => {
        showMentionAutocomplete(textarea);
        showSlashAutocomplete(textarea);
        showChannelAutocomplete(textarea);
        showEmojiAutocomplete(textarea);
      },
      onKeydown: (e, textarea) => {
        if (handleMentionKeydown(e, textarea)) return true;
        if (handleEmojiKeydown(e, textarea)) return true;
        if (handleSlashKeydown(e, textarea)) return true;
        return false;
      },
    });
    const dmInput = dmComposeHandle.textarea;
    dmComposeHandle.emojiBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      showComposeEmojiPicker(e.currentTarget as HTMLElement, dmInput);
    });
    dmComposeHandle.gifBtn?.addEventListener('click', (e) => {
      void openGifPicker(e.currentTarget as HTMLElement, dmInput);
    });
    dmComposeHandle.stickerBtn?.addEventListener('click', (e) => {
      void openStickerPicker(e.currentTarget as HTMLElement, dmInput);
    });
    dmComposeHandle.focus();
  }

  // Scroll-to-bottom button + "X new messages" pill (Discord parity).
  // Pass 12 made DMs preserve scroll position when reading older history —
  // so without these two affordances, new messages arriving while the user
  // is scrolled up would be invisible. Same pattern as chat.ts uses.
  const scrollBtn = document.getElementById('dm-scroll-btn');
  let dmUnseenCount = 0;
  let dmIsAtBottom = true;
  let dmPillEl: HTMLButtonElement | null = null;
  const ensureDmPill = (): HTMLButtonElement => {
    if (dmPillEl) return dmPillEl;
    const p = document.createElement('button');
    p.type = 'button';
    p.className = 'chat-newmsg-pill';
    p.style.display = 'none';
    p.addEventListener('click', () => {
      messagesEl.scrollTo({ top: messagesEl.scrollHeight, behavior: 'smooth' });
    });
    messagesEl.parentElement?.appendChild(p);
    dmPillEl = p;
    return p;
  };
  const updateDmPill = (): void => {
    const p = ensureDmPill();
    if (dmUnseenCount > 0 && !dmIsAtBottom) {
      p.textContent = `${dmUnseenCount} new message${dmUnseenCount === 1 ? '' : 's'} \u2193`;
      p.style.display = 'flex';
    } else {
      p.style.display = 'none';
    }
  };
  messagesEl.addEventListener('scroll', () => {
    dmIsAtBottom = messagesEl.scrollHeight - messagesEl.scrollTop - messagesEl.clientHeight < 100;
    if (scrollBtn) scrollBtn.style.display = dmIsAtBottom ? 'none' : 'flex';
    if (dmIsAtBottom) { dmUnseenCount = 0; updateDmPill(); }
  });
  scrollBtn?.addEventListener('click', () => {
    messagesEl.scrollTo({ top: messagesEl.scrollHeight, behavior: 'smooth' });
    if (scrollBtn) scrollBtn.style.display = 'none';
    dmUnseenCount = 0;
    updateDmPill();
  });
  // Exposed on the element so loadDmMessages can bump the counter when a
  // new message arrived while the user was scrolled up (SSE or 20s poll).
  (messagesEl as HTMLElement & { __hpDmBumpUnseen?: (n: number) => void }).__hpDmBumpUnseen = (delta) => {
    if (dmIsAtBottom) return;
    dmUnseenCount += delta;
    updateDmPill();
  };

  // Load messages
  void loadDmMessages(me, partnerId, messagesEl);

  // Left-click on avatar or author name opens the profile card — Discord parity.
  // Channel chat has the same handler in chat.ts; DMs never had it so clicking
  // a partner's avatar silently did nothing.
  messagesEl.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;

    // Hover quick-bar button? Route before the profile-card path so a click
    // on a button nested above an avatar doesn't double-fire.
    const btn = target.closest('.msg-quick-btn') as HTMLButtonElement | null;
    if (btn) {
      e.stopPropagation();
      const action = btn.dataset['action'];
      const msgId = btn.dataset['msg'] ?? '';
      const msgEl = btn.closest('.msg') as HTMLElement | null;
      if (!msgEl) return;
      if (action === 'copy') {
        const text = msgEl.querySelector('.msg-content')?.textContent ?? '';
        void navigator.clipboard.writeText(text);
        showToast('Copied', 'ok');
      } else if (action === 'copyid') {
        void navigator.clipboard.writeText(msgId);
        showToast('ID copied', 'ok');
      } else if (action === 'more') {
        const rect = btn.getBoundingClientRect();
        msgEl.dispatchEvent(new MouseEvent('contextmenu', {
          bubbles: true, cancelable: true,
          clientX: rect.left, clientY: rect.bottom + 4,
        }));
      }
      return;
    }

    const authorEl = target.classList.contains('msg-author') ? target : null;
    const avatarEl = target.closest('.msg-avatar') as HTMLElement | null;
    const anchor = authorEl || avatarEl;
    if (!anchor) return;
    const msgEl = anchor.closest('.msg');
    const authorId = msgEl?.getAttribute('data-author');
    if (authorId) openProfileCard(authorId, anchor);
  });

  // Right-click context menu on DM messages (1b parity with channel chat)
  messagesEl.addEventListener('contextmenu', (e) => {
    const msgEl = (e.target as HTMLElement).closest('.msg');
    if (!msgEl) return;
    e.preventDefault();
    const text = msgEl.querySelector('.msg-content')?.textContent ?? '';
    const items: ContextMenuItem[] = [
      { label: 'Copy Text', action: () => { void navigator.clipboard.writeText(text); } },
      { label: 'Reply', action: () => {
        const ta = dmComposeHandle?.textarea;
        if (ta) { ta.focus(); ta.value = `> ${text.slice(0, 80)}\n`; ta.dispatchEvent(new Event('input', { bubbles: true })); }
      }},
    ];
    showContextMenu(e.clientX, e.clientY, items);
  });

  // Click partner card or member row → profile card
  document.querySelectorAll<HTMLElement>('.dm-partner-card, #dm-partner-panel .ml-member').forEach(el => {
    el.addEventListener('click', () => {
      const id = el.dataset['id'];
      if (id) openProfileCard(id, el);
    });
  });

  // Close button + persisted open state per-partner
  const panel = document.getElementById('dm-partner-panel');
  const toggleBtn = document.getElementById('dm-members-toggle');
  const persistKey = `hub-people-dm-panel-open:${partnerId}`;
  const savedOpen = localStorage.getItem(persistKey);
  if (savedOpen === 'false') panel?.classList.add('dm-panel-hidden');
  document.getElementById('dm-partner-close')?.addEventListener('click', () => {
    panel?.classList.add('dm-panel-hidden');
    localStorage.setItem(persistKey, 'false');
  });
  toggleBtn?.addEventListener('click', () => {
    if (!panel) return;
    const willHide = !panel.classList.contains('dm-panel-hidden');
    panel.classList.toggle('dm-panel-hidden', willHide);
    localStorage.setItem(persistKey, String(!willHide));
  });

  // Mobile back button — return to DM home (conversation list). Channel chat
  // has the same `.ca-header-back` but its click opens the channel-list drawer;
  // for DMs the "list" IS the content-area DM home, so re-render it in place.
  document.getElementById('dm-header-back')?.addEventListener('click', () => {
    cleanupDms();
    container.innerHTML = '';
    const body = document.createElement('div');
    body.className = 'ca-body ca-body-chat';
    body.style.height = '100%';
    container.appendChild(body);
    renderDmHome(body);
  });

  // Realtime SSE (dm_created) is the primary push path. Keep a slow poll as a
  // safety net for the case where SSE is blocked (strict proxies, offline
  // tabs) — at 20s it's cheap and only catches the tail when realtime fails.
  dmPollTimer = setInterval(() => void loadDmMessages(me, partnerId, messagesEl), 20_000);
}

function renderDmQuickBar(msgId: string): string {
  const iconCopy = `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>`;
  const iconHash = `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></svg>`;
  const iconMore = `<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/></svg>`;
  return `<div class="msg-quick-bar msg-quickbar msg-actions" data-msg-actions role="toolbar" aria-label="Message actions">
    <button class="msg-quick-btn" data-action="copy" data-msg="${esc(msgId)}" title="Copy Text" aria-label="Copy Text">${iconCopy}</button>
    <button class="msg-quick-btn" data-action="copyid" data-msg="${esc(msgId)}" title="Copy Message ID" aria-label="Copy Message ID">${iconHash}</button>
    <button class="msg-quick-btn" data-action="more" data-msg="${esc(msgId)}" title="More" aria-label="More">${iconMore}</button>
  </div>`;
}

/** Discord parity: fetch + render Mutual Servers and Mutual Friends into the
 *  DM right panel. Replaces the old "Connections" (email+phone) section that
 *  Discord never shows — private contact info stays private. */
async function loadMutualData(partnerId: string, viewerId: string): Promise<void> {
  const serversEl = document.getElementById('dm-mutual-servers');
  const friendsEl = document.getElementById('dm-mutual-friends');
  if (!serversEl || !friendsEl) return;
  try {
    const data = await get<{ servers: { id: string; name: string; icon: string }[]; friends: { id: string; name: string; avatar: string }[] }>(
      `/api/people/${encodeURIComponent(partnerId)}/mutual?viewer=${encodeURIComponent(viewerId)}`,
    );
    if (data.servers.length === 0) {
      serversEl.innerHTML = '<div style="color:var(--fg-muted);font-size:12px;padding:4px 10px">No servers in common</div>';
    } else {
      serversEl.innerHTML = data.servers.map(s => {
        const initial = (s.name || '?').charAt(0).toUpperCase();
        const icon = s.icon && s.icon.startsWith('/')
          ? `<img src="${esc(s.icon)}" style="width:100%;height:100%;object-fit:cover;border-radius:50%" alt="">`
          : `<span>${esc(initial)}</span>`;
        return `<div class="dm-mutual-row" data-server="${esc(s.id)}"><div class="dm-mutual-icon">${icon}</div><div class="dm-mutual-name">${esc(s.name)}</div></div>`;
      }).join('');
    }
    if (data.friends.length === 0) {
      friendsEl.innerHTML = '<div style="color:var(--fg-muted);font-size:12px;padding:4px 10px">No friends in common</div>';
    } else {
      friendsEl.innerHTML = data.friends.map(f => {
        const initial = (f.name || '?').charAt(0).toUpperCase();
        return `<div class="dm-mutual-row"><div class="dm-mutual-icon"><span>${esc(initial)}</span></div><div class="dm-mutual-name">${esc(f.name)}</div></div>`;
      }).join('');
    }
  } catch {
    serversEl.innerHTML = '<div style="color:var(--fg-muted);font-size:12px;padding:4px 10px">No servers in common</div>';
    friendsEl.innerHTML = '<div style="color:var(--fg-muted);font-size:12px;padding:4px 10px">No friends in common</div>';
  }
}

async function loadDmMessages(myId: string, partnerId: string, el: HTMLElement): Promise<void> {
  try {
    const msgs = await get<DirectMessage[]>(
      `/api/dms/messages?person_id=${encodeURIComponent(myId)}&partner_id=${encodeURIComponent(partnerId)}&limit=50`,
    );
    const sorted = msgs.reverse();

    if (sorted.length === 0) {
      el.innerHTML = `<div class="chat-empty">
        <div class="chat-empty-title">Start of conversation</div>
        <div class="chat-empty-sub">This is the beginning of your DM with ${esc(authorName(partnerId))}.</div>
      </div>`;
      return;
    }

    let html = '';
    let lastDate = '';
    for (let i = 0; i < sorted.length; i++) {
      const msg = sorted[i] as DirectMessage;
      const prev = i > 0 ? (sorted[i - 1] as DirectMessage) : null;
      const msgDate = fmtDayHeading(msg.created_at);
      if (msgDate && msgDate !== lastDate) {
        html += `<div class="msg-date-divider"><span>${esc(msgDate)}</span></div>`;
        lastDate = msgDate;
      }
      const showHeader = !prev || prev.from_id !== msg.from_id;
      const name = authorName(msg.from_id);
      const initial = (name || '?').charAt(0).toUpperCase();
      const time = formatTime(msg.created_at);

      const content = msg.content ? renderMarkdown(msg.content) : '';
      const quickBar = renderDmQuickBar(msg.id);
      if (showHeader) {
        html += `<div class="msg" data-id="${esc(msg.id)}" data-author="${esc(msg.from_id)}">
          <div class="msg-avatar" style="background:${avatarBg(msg.from_id)}">${esc(initial)}</div>
          <div class="msg-body">
            <div class="msg-header">
              <span class="msg-author" data-author="${esc(msg.from_id)}">${esc(name)}</span>
              <span class="msg-time">${esc(time)}</span>
            </div>
            <div class="msg-content">${content}</div>
          </div>
          ${quickBar}
        </div>`;
      } else {
        html += `<div class="msg msg-compact" data-id="${esc(msg.id)}" data-author="${esc(msg.from_id)}">
          <div class="msg-body"><div class="msg-content">${content}</div></div>
          ${quickBar}
        </div>`;
      }
    }
    // Preserve scroll position on refresh. Every `loadDmMessages` was
    // yanking the user to the bottom — including the 20s safety poll and
    // SSE `dm_created` re-renders — so anyone scrolled up reading older
    // DMs got bounced to latest on every tick. Channel chat uses the same
    // `isAtBottom` guard (chat.ts).
    const wasFirstLoad = !el.querySelector('.msg');
    const priorFromBottom = wasFirstLoad ? 0 : el.scrollHeight - el.scrollTop - el.clientHeight;
    const wasAtBottom = priorFromBottom < 100;
    const oldLastId = el.querySelector<HTMLElement>('.msg:last-of-type')?.dataset['id'] ?? '';
    el.innerHTML = html;
    if (wasFirstLoad || wasAtBottom) {
      el.scrollTop = el.scrollHeight;
    } else {
      el.scrollTop = Math.max(0, el.scrollHeight - priorFromBottom - el.clientHeight);
      // User was reading older history — count how many NEW foreign messages
      // arrived and bump the "N new messages ↓" pill via openDmConversation's
      // closure. Own-sent messages are excluded because the sender knows.
      const lastIdx = oldLastId ? sorted.findIndex(m => m.id === oldLastId) : -1;
      const newOnes = sorted.slice(lastIdx + 1);
      const delta = newOnes.filter(m => m.from_id !== myId).length;
      if (delta > 0) {
        const bump = (el as HTMLElement & { __hpDmBumpUnseen?: (n: number) => void }).__hpDmBumpUnseen;
        bump?.(delta);
      }
    }
  } catch {
    el.innerHTML = '<div class="chat-loading">Failed to load messages</div>';
  }
}

async function sendDm(myId: string, partnerId: string, text: string, el: HTMLElement): Promise<void> {
  const content = text.trim();
  if (!content) return;

  try {
    await post('/api/dms/send', { from_id: myId, to_id: partnerId, content });
    dmComposeHandle?.clearDraft();
    dmComposeHandle?.resetInput();
    // Force scroll to bottom after a send — the user expects to see what
    // they just sent regardless of where the scrollback happened to be.
    await loadDmMessages(myId, partnerId, el);
    el.scrollTop = el.scrollHeight;
  } catch (e) {
    showToast(String(e), 'err');
    if (dmComposeHandle) { dmComposeHandle.textarea.value = content; dmComposeHandle.textarea.focus(); }
  }
}

export function cleanupDms(): void {
  if (dmPollTimer) { clearInterval(dmPollTimer); dmPollTimer = null; }
  dmComposeHandle?.destroy();
  dmComposeHandle = null;
  openDmContext = null;
  cleanupVpsStatsWidget();
  const dsHost = document.getElementById('dm-dev-sessions');
  if (dsHost) cleanupDevSessionWidgets(dsHost);
}

/* ──────────────────────────────────────────────────────────────────────
 * VPS Stats Widget — 4 tiles (CPU, RAM, Disk, Containers)
 * Polls /api/vps-stats every 30s; pauses when tab hidden.
 * ────────────────────────────────────────────────────────────────────── */

interface VpsStats {
  updated_at: string;
  uptime_seconds: number;
  load_1m: number;
  load_5m: number;
  load_15m: number;
  cpu_count: number;
  mem_total_mb: number;
  mem_used_mb: number;
  mem_free_mb: number;
  swap_total_mb: number;
  swap_used_mb: number;
  disk_total_gb: number;
  disk_used_gb: number;
  disk_free_gb: number;
  container_count: number;
  container_unhealthy: number;
  stale: boolean;
}

let vpsPollTimer: ReturnType<typeof setInterval> | null = null;
let vpsVisHandler: (() => void) | null = null;

function tierColor(pct: number): string {
  if (pct >= 90) return 'danger';
  if (pct >= 70) return 'warning';
  return 'accent';
}

function tierClass(pct: number): string {
  if (pct >= 90) return 'ds-tile-red';
  if (pct >= 70) return 'ds-tile-amber';
  return 'ds-tile-green';
}

function formatUptime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function renderBar(pct: number, color: string): string {
  return `<div class="vs-bar" title="${pct.toFixed(1)}%">
    <div class="vs-bar-fill" style="width:${Math.min(pct, 100)}%;background:var(--${color})"></div>
  </div>`;
}

function renderVpsStatsContent(host: HTMLElement, data: VpsStats): void {
  const cpuPct = data.cpu_count > 0 ? (data.load_1m / data.cpu_count) * 100 : 0;
  const ramPct = data.mem_total_mb > 0 ? (data.mem_used_mb / data.mem_total_mb) * 100 : 0;
  const diskPct = data.disk_total_gb > 0 ? (data.disk_used_gb / data.disk_total_gb) * 100 : 0;
  const containerOk = data.container_unhealthy === 0;

  const staleOverlay = data.stale
    ? '<div class="vs-stale">Stale — collector offline</div>'
    : '';

  host.innerHTML = `
    <div class="vs-grid ${data.stale ? 'vs-stale-row' : ''}">
      ${staleOverlay}
      <div class="ds-tile ${tierClass(cpuPct)}" title="Load: ${data.load_1m} / ${data.load_5m} / ${data.load_15m} (1m/5m/15m) — ${data.cpu_count} cores">
        <div class="ds-tile-head">
          <span class="ds-tile-name">CPU</span>
          <span class="ds-tile-agent">${cpuPct.toFixed(0)}%</span>
        </div>
        ${renderBar(cpuPct, tierColor(cpuPct))}
        <div class="ds-tile-meta">
          Load ${data.load_1m} / ${data.load_5m} / ${data.load_15m}
        </div>
      </div>
      <div class="ds-tile ${tierClass(ramPct)}" title="${(data.mem_used_mb/1024).toFixed(1)} GB used / ${(data.mem_total_mb/1024).toFixed(1)} GB total — Swap: ${(data.swap_used_mb/1024).toFixed(2)} GB / ${(data.swap_total_mb/1024).toFixed(2)} GB">
        <div class="ds-tile-head">
          <span class="ds-tile-name">RAM</span>
          <span class="ds-tile-agent">${ramPct.toFixed(0)}%</span>
        </div>
        ${renderBar(ramPct, tierColor(ramPct))}
        <div class="ds-tile-meta">
          ${(data.mem_used_mb/1024).toFixed(1)} / ${(data.mem_total_mb/1024).toFixed(1)} GB
        </div>
      </div>
      <div class="ds-tile ${tierClass(diskPct)}" title="${data.disk_used_gb} GB used / ${data.disk_total_gb} GB total — ${data.disk_free_gb} GB free">
        <div class="ds-tile-head">
          <span class="ds-tile-name">Disk</span>
          <span class="ds-tile-agent">${diskPct.toFixed(0)}%</span>
        </div>
        ${renderBar(diskPct, tierColor(diskPct))}
        <div class="ds-tile-meta">
          ${data.disk_used_gb} / ${data.disk_total_gb} GB
        </div>
      </div>
      <div class="ds-tile ${containerOk ? 'ds-tile-green' : 'ds-tile-amber'}" title="${data.container_count} containers — ${data.container_unhealthy} unhealthy — uptime ${formatUptime(data.uptime_seconds)}">
        <div class="ds-tile-head">
          <span class="ds-tile-name">Containers</span>
          <span class="ds-tile-agent">${data.container_count}</span>
        </div>
        <div class="ds-tile-meta" style="font-size:13px;font-weight:600;color:var(--${containerOk ? 'accent' : 'warning'})">
          ${containerOk ? 'All healthy' : `${data.container_unhealthy} unhealthy`}
        </div>
        <div class="ds-tile-meta">
          Uptime ${formatUptime(data.uptime_seconds)}
        </div>
      </div>
    </div>`;
}

function vpsStatsLooksValid(d: unknown): d is VpsStats {
  if (!d || typeof d !== 'object') return false;
  const o = d as Record<string, unknown>;
  return typeof o['cpu_count'] === 'number'
      && typeof o['mem_total_mb'] === 'number'
      && typeof o['disk_total_gb'] === 'number'
      && typeof o['container_count'] === 'number';
}

async function fetchAndRenderVps(host: HTMLElement): Promise<void> {
  try {
    const data = await get<VpsStats>('/api/vps-stats');
    if (!vpsStatsLooksValid(data)) {
      host.innerHTML = '<div class="ds-widget-empty">VPS stats unavailable</div>';
      return;
    }
    renderVpsStatsContent(host, data);
  } catch {
    host.innerHTML = '<div class="ds-widget-empty">VPS stats unavailable</div>';
  }
}

function renderVpsStatsWidget(host: HTMLElement): void {
  host.innerHTML = '<div class="ds-widget-loading">Loading VPS stats...</div>';
  void fetchAndRenderVps(host);

  vpsPollTimer = setInterval(() => {
    if (document.visibilityState === 'visible') {
      void fetchAndRenderVps(host);
    }
  }, 30_000);

  vpsVisHandler = () => {
    if (document.visibilityState === 'visible') {
      void fetchAndRenderVps(host);
    }
  };
  document.addEventListener('visibilitychange', vpsVisHandler);
}

function cleanupVpsStatsWidget(): void {
  if (vpsPollTimer) { clearInterval(vpsPollTimer); vpsPollTimer = null; }
  if (vpsVisHandler) { document.removeEventListener('visibilitychange', vpsVisHandler); vpsVisHandler = null; }
}
